import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getOrganization, getCertifications, getTools, getReports, getNotifications,
  downloadCertificatePdf, downloadReportPdf, markNotificationRead, getMyApplication,
  createCheckoutSession, createRenewalCheckout, getRenewalInfo
} from '../lib/api';
import { isAuthenticated, clearAuth, getUser, getUserName, getUserOrgId } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { formatDate, getStatusClass, downloadBlob } from '../lib/utils';
import { 
  Building2, Award, FileText, Wrench, LogOut, Download, Play, ExternalLink, Bell,
  CreditCard, CheckCircle, Clock, XCircle, ArrowRight, RefreshCw
} from 'lucide-react';

export default function OrgDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('status');
  const [organization, setOrganization] = useState(null);
  const [declarations, setdeclarations] = useState([]);
  const [tools, setTools] = useState([]);
  const [reports, setReports] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [application, setApplication] = useState(null);
  const [renewalInfo, setRenewalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [renewalLoading, setRenewalLoading] = useState(false);

  const user = getUser();
  const orgId = getUserOrgId();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/org/login');
      return;
    }
    if (!orgId) {
      navigate('/org/login');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally run once on mount/orgId change - loadData is defined inline
  }, [navigate, orgId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [appRes, toolsRes, notifsRes] = await Promise.all([
        getMyApplication(),
        getTools('active'),
        getNotifications()
      ]);
      
      setOrganization(appRes.data.organization);
      setApplication(appRes.data);
      setTools(toolsRes.data);
      setNotifications(notifsRes.data);
      
      // Only load certs and reports if org is active
      if (appRes.data.organization?.status === 'active') {
        const [certsRes, reportsRes, renewalRes] = await Promise.all([
          getCertifications(orgId),
          getReports(),
          getRenewalInfo()
        ]);
        setdeclarations(certsRes.data);
        setReports(reportsRes.data);
        setRenewalInfo(renewalRes.data);
        setActiveTab('profile');
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        clearAuth();
        navigate('/org/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/org/login');
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await createCheckoutSession();
      window.location.href = response.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create payment session');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleRenewal = async () => {
    setRenewalLoading(true);
    try {
      const response = await createRenewalCheckout();
      window.location.href = response.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create renewal session');
    } finally {
      setRenewalLoading(false);
    }
  };

  const handleDownloadCert = async (certId, declarationId) => {
    try {
      const response = await downloadCertificatePdf(certId);
      downloadBlob(response.data, `certificate-${declarationId}.pdf`);
    } catch (err) {
      alert('Failed to download certificate');
    }
  };

  const handleDownloadReport = async (reportId, reportIdStr) => {
    try {
      const response = await downloadReportPdf(reportId);
      downloadBlob(response.data, `report-${reportIdStr}.pdf`);
    } catch (err) {
      alert('Failed to download report');
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await markNotificationRead(notifId);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const orgStatus = organization?.status;
  const isPending = ['pending_payment', 'pending_assessment', 'pending_review'].includes(orgStatus);
  const isDenied = orgStatus === 'denied';
  
  // Calculate assessment progress
  const requiredTools = application?.application?.required_tool_ids || [];
  const completedTools = application?.completed_assessments || [];
  const assessmentProgress = requiredTools.length > 0 
    ? Math.round((completedTools.length / requiredTools.length) * 100) 
    : 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  // Status-based content for pending/denied states
  const renderStatusContent = () => {
    if (isDenied) {
      return (
        <div className="max-w-2xl mx-auto mt-12">
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <XCircle className="w-6 h-6" />
                Application Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-600 mb-4">
                Unfortunately, your declaration application has been denied.
              </p>
              {application?.application?.denial_reason && (
                <div className="bg-red-50 border border-red-200 p-4 mb-4">
                  <p className="text-sm font-medium text-red-800">Reason:</p>
                  <p className="text-red-700">{application.application.denial_reason}</p>
                </div>
              )}
              <p className="text-sm text-zinc-500">
                If you believe this was in error or would like to discuss, please contact support.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (orgStatus === 'pending_payment') {
      return (
        <div className="max-w-2xl mx-auto mt-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-6 h-6" />
                Payment Required
              </CardTitle>
              <CardDescription>
                Complete payment to proceed with your declaration application
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-zinc-50 border border-zinc-200 p-4">
                  <p className="text-sm text-zinc-600 mb-2">Standards applied for:</p>
                  <ul className="space-y-1">
                    {application?.application?.standard_ids?.map(stdId => (
                      <li key={stdId} className="text-zinc-900 font-medium">{stdId}</li>
                    ))}
                  </ul>
                </div>
                
                {application?.payment && (
                  <div className="flex justify-between items-center py-4 border-y border-zinc-200">
                    <span className="font-medium">Total Amount</span>
                    <span className="text-2xl font-bold">${application.payment.payment_amount?.toLocaleString()}</span>
                  </div>
                )}
                
                <Button 
                  onClick={handlePayment} 
                  disabled={paymentLoading}
                  className="w-full"
                  data-testid="pay-now-btn"
                >
                  {paymentLoading ? 'Redirecting to Payment...' : 'Pay Now'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (orgStatus === 'pending_assessment') {
      return (
        <div className="max-w-3xl mx-auto mt-12 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                Payment Confirmed
              </CardTitle>
              <CardDescription>
                Complete the required assessments to submit your application for review
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-zinc-600">Assessment Progress</span>
                    <span className="text-sm font-medium">{completedTools.length} / {requiredTools.length}</span>
                  </div>
                  <Progress value={assessmentProgress} className="h-2" />
                </div>
                
                {requiredTools.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      No assessments required for your selected standards. Your application will be submitted for review automatically.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-zinc-700">Required Assessments:</p>
                    {requiredTools.map(toolId => {
                      const tool = tools.find(t => t.id === toolId);
                      const isComplete = completedTools.includes(toolId);
                      return (
                        <div 
                          key={toolId} 
                          className={`flex items-center justify-between p-4 border ${
                            isComplete ? 'border-green-200 bg-green-50' : 'border-zinc-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {isComplete ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-zinc-300" />
                            )}
                            <div>
                              <p className="font-medium">{tool?.name || toolId}</p>
                              <p className="text-sm text-zinc-500">{tool?.standard_title}</p>
                            </div>
                          </div>
                          {!isComplete && (
                            <Button 
                              onClick={() => navigate(`/tools/${toolId}/run`)}
                              size="sm"
                              data-testid={`run-assessment-${toolId}`}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Run
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (orgStatus === 'pending_review') {
      return (
        <div className="max-w-2xl mx-auto mt-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-amber-500" />
                Under Review
              </CardTitle>
              <CardDescription>
                Your application is being reviewed by an administrator
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 p-4">
                <p className="text-amber-800">
                  All required assessments have been completed. Your application is now in the review queue. 
                  You will be notified once a decision has been made.
                </p>
              </div>
              <div className="mt-4 text-sm text-zinc-500">
                <p>While you wait:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Check your notifications for updates</li>
                  <li>Review your submitted assessment reports</li>
                  <li>Ensure your organization details are accurate</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="org-dashboard">
      {/* Header */}
      <header className="header-bar">
        <div className="flex items-center gap-4">
          <span className="header-logo">AIAS</span>
          <span className="text-zinc-300">|</span>
          <span className="text-sm text-zinc-500">{organization?.display_name || 'Organization'}</span>
          {isPending && (
            <Badge variant="outline" className="rounded-none bg-blue-50 text-blue-700 border-blue-200">
              Application Pending
            </Badge>
          )}
          {isDenied && (
            <Badge variant="outline" className="rounded-none bg-red-50 text-red-700 border-red-200">
              Denied
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-zinc-600">{getUserName()}</span>
          <Button variant="ghost" onClick={handleLogout} className="text-zinc-600" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-8">
        {/* Show status-based content for pending/denied states */}
        {(isPending || isDenied) && renderStatusContent()}
        
        {/* Show full dashboard for active organizations */}
        {orgStatus === 'active' && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 h-12 rounded-none border border-zinc-200 bg-zinc-50 p-1">
              <TabsTrigger value="profile" className="rounded-none data-[state=active]:bg-white" data-testid="tab-profile">
                <Building2 className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="declarations" className="rounded-none data-[state=active]:bg-white" data-testid="tab-declarations">
                <Award className="w-4 h-4 mr-2" />
                Declarations
              </TabsTrigger>
              <TabsTrigger value="tools" className="rounded-none data-[state=active]:bg-white" data-testid="tab-tools">
                <Wrench className="w-4 h-4 mr-2" />
                Tools
              </TabsTrigger>
              <TabsTrigger value="reports" className="rounded-none data-[state=active]:bg-white" data-testid="tab-reports">
                <FileText className="w-4 h-4 mr-2" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-none data-[state=active]:bg-white relative" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="mt-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Organization Profile
                  </h2>
                </div>
                
                <div className="border border-zinc-200 divide-y divide-zinc-200">
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Display Name</span>
                    <span className="font-medium">{organization?.display_name}</span>
                  </div>
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Legal Name</span>
                    <span>{organization?.legal_name}</span>
                  </div>
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Contact Email</span>
                    <span className="font-mono text-sm">{organization?.contact_email}</span>
                  </div>
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Website</span>
                    <span>{organization?.website || '-'}</span>
                  </div>
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Status</span>
                    <Badge variant="outline" className={`rounded-none ${getStatusClass(organization?.status)}`}>
                      {organization?.status}
                    </Badge>
                  </div>
                  <div className="p-4 flex">
                    <span className="w-40 text-sm text-zinc-500">Member Since</span>
                    <span className="font-mono text-sm">{formatDate(organization?.created_at)}</span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Declarations Tab */}
            <TabsContent value="declarations" className="mt-6">
              <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Your Declarations
              </h2>
              
              {declarations.length === 0 ? (
                <div className="empty-state border border-zinc-200 p-12">
                  <Award className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                  <p>No declarations yet.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {declarations.map(cert => (
                    <div key={cert.id} className="border border-zinc-200 p-6 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="cert-id text-lg">{cert.declaration_id}</span>
                          <Badge variant="outline" className={`rounded-none ${getStatusClass(cert.status)}`}>
                            {cert.status}
                          </Badge>
                        </div>
                        <p className="font-medium">{cert.standard_title}</p>
                        <p className="text-sm text-zinc-500 mt-1">
                          Issued: {formatDate(cert.issue_date)} · Expires: {formatDate(cert.expiry_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => handleDownloadCert(cert.id, cert.declaration_id)}
                          className="rounded-none"
                          data-testid={`download-cert-${cert.id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                        <Button 
                          variant="ghost"
                          onClick={() => window.open(`/verify/${cert.declaration_id}`, '_blank')}
                          className="rounded-none"
                          data-testid={`verify-cert-${cert.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Renewal Section */}
              {renewalInfo && renewalInfo.can_renew && (
                <Card className="mt-6 border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <RefreshCw className="w-5 h-5" />
                      Declaration Renewal
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      {renewalInfo.days_until_expiry <= 0 
                        ? 'Your declaration has expired. Renew now to maintain your listing.'
                        : renewalInfo.days_until_expiry <= 30
                          ? `Your declaration expires in ${renewalInfo.days_until_expiry} days.`
                          : `Your declaration is valid until ${formatDate(renewalInfo.expiry_date)}.`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-800">Renewal Fee: <strong>${renewalInfo.renewal_fee}</strong></p>
                        <p className="text-xs text-amber-600">Extends declaration for 12 months</p>
                      </div>
                      <Button 
                        onClick={handleRenewal}
                        disabled={renewalLoading}
                        className="rounded-none bg-amber-600 hover:bg-amber-700"
                        data-testid="renew-cert-btn"
                      >
                        {renewalLoading ? (
                          <>Processing...</>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Renew Now
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Tools Tab */}
            <TabsContent value="tools" className="mt-6">
              <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Verified Instruments
              </h2>
              <p className="text-zinc-500 mb-6">
                Run verified assessment tools to generate standardized reports.
              </p>
              
              <div className="card-grid">
                {tools.map(tool => (
                  <div key={tool.id} className="tool-card">
                    <div className="cert-id text-xs mb-2">{tool.id}</div>
                    <h3 className="tool-card-title">{tool.name}</h3>
                    <p className="tool-card-description">{tool.description}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-200">
                      <span className="text-xs text-zinc-500">{tool.standard_title}</span>
                      <Button 
                        onClick={() => navigate(`/tools/${tool.id}/run`)}
                        className="btn-primary rounded-none"
                        data-testid={`run-tool-${tool.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Run
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="mt-6">
              <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Generated Reports
              </h2>
              
              {reports.length === 0 ? (
                <div className="empty-state border border-zinc-200 p-12">
                  <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                  <p>No reports generated yet.</p>
                  <Button 
                    onClick={() => setActiveTab('tools')} 
                    variant="outline" 
                    className="mt-4 rounded-none"
                  >
                    Run a Tool
                  </Button>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Report ID</th>
                        <th>Tool</th>
                        <th>Standard</th>
                        <th>Generated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map(report => (
                        <tr key={report.id}>
                          <td className="cert-id">{report.report_id}</td>
                          <td>{report.tool_name}</td>
                          <td>{report.standard_title}</td>
                          <td>{formatDate(report.created_at)}</td>
                          <td className="space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/reports/${report.report_id}`)}
                              data-testid={`view-report-${report.id}`}
                            >
                              View
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadReport(report.id, report.report_id)}
                              data-testid={`download-report-${report.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="mt-6">
              <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Notifications
              </h2>
              
              {notifications.length === 0 ? (
                <div className="empty-state border border-zinc-200 p-12">
                  <Bell className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                  <p>No notifications.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map(notif => (
                    <div 
                      key={notif.id} 
                      className={`border p-4 ${notif.is_read ? 'border-zinc-200 bg-white' : 'border-blue-200 bg-blue-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium">{notif.title}</h3>
                            {!notif.is_read && (
                              <Badge variant="outline" className="rounded-none bg-blue-100 text-blue-700 border-blue-200 text-xs">
                                New
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-zinc-600">{notif.message}</p>
                          <p className="text-xs text-zinc-400 mt-2">{formatDate(notif.created_at)}</p>
                        </div>
                        {!notif.is_read && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleMarkRead(notif.id)}
                            className="text-zinc-500"
                            data-testid={`mark-read-${notif.id}`}
                          >
                            Mark read
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
        
        {/* Show notifications for pending states too */}
        {isPending && notifications.length > 0 && (
          <div className="max-w-2xl mx-auto mt-8">
            <h3 className="text-lg font-medium mb-4">Recent Notifications</h3>
            <div className="space-y-3">
              {notifications.slice(0, 3).map(notif => (
                <div 
                  key={notif.id} 
                  className={`border p-3 ${notif.is_read ? 'border-zinc-200' : 'border-blue-200 bg-blue-50'}`}
                >
                  <p className="font-medium text-sm">{notif.title}</p>
                  <p className="text-sm text-zinc-600">{notif.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
